import { createSignal, onMount, Show, For } from "solid-js";
import Text from "../components/Text";
import PushNotificationToggle from "../components/PushNotificationToggle";
import InstallAppButton from "../components/InstallAppButton";
import { installAvailable, installed } from "../services/pwa";
import { useMutedContacts } from "../state/mutedContacts";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { useUserContext } from "../components/UserContextProvider";
import { isPrettifyEnabled, setPrettifyEnabled } from "../services/quantityFormatter";
import { messageSoundEnabled, setMessageSoundEnabled, playMessageSound } from "../services/messageSound";
import { useTheme, type ThemeMode } from "../hooks/useTheme";
import { useApi, isOk } from "../api";
import { useToast } from "../components/MessageBox";
import { useTranslation } from "../hooks/useTranslation";
import en from "../en.json";
import de from "../de.json";

const LANGUAGES = [
  { key: "en", label: "English", data: en },
  { key: "de", label: "Deutsch", data: de },
];

export default function Settings() {
  const { user, switchLanguage } = useUserContext();
  const { mode: themeMode, setMode: setThemeMode } = useTheme();
  const api = useApi();
  const toast = useToast();
  const { t } = useTranslation();

  const mutedContacts = useMutedContacts();
  const msgsEnabled = useFeatureFlag("MSGS");

  const [prettify, setPrettify] = createSignal(isPrettifyEnabled());
  const [msgPush, setMsgPush] = createSignal(false);
  const [msgPushBusy, setMsgPushBusy] = createSignal(false);
  const [muteInput, setMuteInput] = createSignal("");
  const [addingMute, setAddingMute] = createSignal(false);

  async function addMutedContact() {
    const name = muteInput().trim();
    if (!name || addingMute()) return;
    setAddingMute(true);
    const ok = await mutedContacts.mute(name);
    setAddingMute(false);
    if (ok) { setMuteInput(""); toast?.success(t("SETTINGS_SAVED")); }
    else toast?.error(t("SETTINGS_SAVE_FAILED"));
  }

  async function removeMutedContact(name: string) {
    const ok = await mutedContacts.unmute(name);
    if (ok) toast?.info(t("SETTINGS_SAVED"));
    else toast?.error(t("SETTINGS_SAVE_FAILED"));
  }

  onMount(async () => {
    const res = await api.push.getPreferences();
    if (isOk(res.data)) setMsgPush(res.data.value.enabled);
  });

  function handlePrettifyToggle() {
    const next = !prettify();
    setPrettify(next);
    setPrettifyEnabled(next);
  }

  async function handleMsgPushToggle() {
    if (msgPushBusy()) return;
    const next = !msgPush();
    setMsgPush(next);
    setMsgPushBusy(true);
    const res = await api.push.setPreferences({ enabled: next });
    setMsgPushBusy(false);
    if (isOk(res.data)) toast?.success(t("SETTINGS_SAVED"));
    else { setMsgPush(!next); toast?.error(t("SETTINGS_SAVE_FAILED")); }
  }

  function handleSoundToggle() {
    setMessageSoundEnabled(!messageSoundEnabled());
  }

  function currentLanguageKey() {
    // Read from cookie first (source of truth set by LanguageChooser)
    const cookie = document.cookie.split('; ').find(r => r.startsWith('lang='));
    if (cookie) {
      const val = cookie.split('=')[1];
      if (val === "at") return "de";
      return val;
    }
    const lang = user()?.language;
    if (!lang) return "en";
    const found = LANGUAGES.find(l => l.data === lang);
    return found?.key ?? "en";
  }

  function handleLanguageChange(key: string) {
    const lang = LANGUAGES.find(l => l.key === key);
    if (lang) {
      document.cookie = `lang=${key}; path=/; max-age=31536000`;
      switchLanguage(lang.data);
      try { localStorage.setItem("vektor_language", key); } catch {}
    }
  }

  return (
    <div class="dashboard-page">
      <h1><Text val="SETTINGS" /></h1>

      <div class="page-card">
        <div class="page-section">
          <h2><i class="fa-solid fa-mobile-screen-button" style={{ "margin-right": "8px", opacity: "0.6" }}></i><Text val="SETTINGS_INSTALL" /></h2>
          <div class="v" style={{ gap: "var(--spacing-md)", "max-width": "520px" }}>
            <Show when={installed()}>
              <span style={{ display: "inline-flex", "align-items": "center", gap: "var(--spacing-xs)", "font-size": "var(--font-size-sm)", color: "var(--success-500)" }}>
                <i class="fa-solid fa-circle-check" />
                <Text val="SETTINGS_PWA_INSTALLED" />
              </span>
            </Show>
            <Show when={!installed() && installAvailable()}>
              <span class="gray" style={{ "font-size": "var(--font-size-sm)" }}><Text val="SETTINGS_INSTALL_DESC" /></span>
              <InstallAppButton />
            </Show>
            <Show when={!installed() && !installAvailable()}>
              <span style={{ display: "inline-flex", "align-items": "center", gap: "var(--spacing-xs)", "font-size": "var(--font-size-sm)", color: "var(--dark-text-muted)" }}>
                <i class="fa-solid fa-circle-xmark" />
                <Text val="SETTINGS_PWA_NOT_INSTALLED" />
              </span>
            </Show>
          </div>
        </div>
      </div>

      <div class="page-card">
        <div class="page-section">
          <h2><i class="fa-solid fa-display" style={{ "margin-right": "8px", opacity: "0.6" }}></i><Text val="SETTINGS_DISPLAY" /></h2>

          <div class="field-group">
            <span class="field-label"><Text val="SETTINGS_LANGUAGE" /></span>
            <select
              value={currentLanguageKey()}
              onChange={(e) => handleLanguageChange(e.currentTarget.value)}
              style={{ "max-width": "300px" }}
            >
              {LANGUAGES.map(l => <option value={l.key}>{l.label}</option>)}
            </select>
          </div>

          <div class="field-group">
            <span class="field-label"><Text val="SETTINGS_THEME" /></span>
            <select
              value={themeMode()}
              onChange={(e) => setThemeMode(e.currentTarget.value as ThemeMode)}
              style={{ "max-width": "300px" }}
            >
              <option value="auto">{t("SETTINGS_THEME_AUTO")}</option>
              <option value="dark">{t("SETTINGS_THEME_DARK")}</option>
              <option value="light">{t("SETTINGS_THEME_LIGHT")}</option>
            </select>
          </div>
        </div>
      </div>

      <div class="page-card">
        <div class="page-section">
          <h2><i class="fa-solid fa-bell" style={{ "margin-right": "8px", opacity: "0.6" }}></i><Text val="SETTINGS_NOTIFICATIONS" /></h2>
          <PushNotificationToggle />
        </div>
      </div>

      <div class="page-card">
        <div class="page-section">
          <h2><i class="fa-solid fa-comment-dots" style={{ "margin-right": "8px", opacity: "0.6" }}></i><Text val="SETTINGS_MSG_PUSH" /></h2>

          <label class="permission-item" style={{ cursor: msgPushBusy() ? "wait" : "pointer", "max-width": "fit-content" }} onClick={() => void handleMsgPushToggle()}>
            <input type="checkbox" checked={msgPush()} disabled={msgPushBusy()} />
            <div class="v" style={{ gap: "2px" }}>
              <span><Text val="SETTINGS_MSG_PUSH_LABEL" /></span>
              <span class="gray" style={{ "font-size": "var(--font-size-xs)", "font-weight": "normal" }}>
                <Text val="SETTINGS_MSG_PUSH_DESC" />
              </span>
            </div>
          </label>
        </div>
      </div>

      <Show when={msgsEnabled()}>
        <div class="page-card">
          <div class="page-section">
            <h2><i class="fa-solid fa-user-slash" style={{ "margin-right": "8px", opacity: "0.6" }}></i><Text val="SETTINGS_MUTED_CONTACTS" /></h2>

            <div class="v" style={{ gap: "var(--spacing-md)", "max-width": "520px" }}>
              <span class="gray" style={{ "font-size": "var(--font-size-sm)" }}><Text val="SETTINGS_MUTED_CONTACTS_DESC" /></span>

              <form class="h" style={{ gap: "var(--spacing-sm)" }} onSubmit={(e) => { e.preventDefault(); void addMutedContact(); }}>
                <input
                  type="text"
                  value={muteInput()}
                  onInput={(e) => setMuteInput(e.currentTarget.value)}
                  placeholder={t("SETTINGS_MUTED_CONTACTS_PLACEHOLDER")}
                  style={{ flex: 1 }}
                />
                <button type="submit" class="btn btn-secondary" disabled={!muteInput().trim() || addingMute()}>
                  <Show when={addingMute()} fallback={<><i class="fa-solid fa-plus" /> <Text val="SETTINGS_MUTED_CONTACTS_ADD" /></>}>
                    <i class="fa-solid fa-spinner fa-spin" />
                  </Show>
                </button>
              </form>

              <Show when={!mutedContacts.loading()} fallback={<i class="fa-solid fa-spinner fa-spin" />}>
                <Show
                  when={mutedContacts.mutedNames().length > 0}
                  fallback={
                    <div class="fcard hcenter" style={{ gap: "var(--spacing-sm)" }}>
                      <i class="fa-solid fa-bell" style={{ opacity: 0.6 }} />
                      <span class="gray" style={{ "font-size": "var(--font-size-sm)" }}><Text val="SETTINGS_MUTED_CONTACTS_EMPTY" /></span>
                    </div>
                  }
                >
                  <div class="v" style={{ gap: "var(--spacing-xs)" }}>
                    <For each={mutedContacts.mutedNames()}>{(name) => (
                      <div class="fcard hcenter" style={{ "justify-content": "space-between", padding: "var(--spacing-sm) var(--spacing-md)" }}>
                        <span style={{ "font-family": "var(--font-family-mono)" }}>{name}</span>
                        <button class="btn btn-icon btn-sm" title={t("SETTINGS_MUTED_CONTACTS_UNMUTE")} onClick={() => void removeMutedContact(name)}>
                          <i class="fa-solid fa-xmark" />
                        </button>
                      </div>
                    )}</For>
                  </div>
                </Show>
              </Show>
            </div>
          </div>
        </div>
      </Show>

      <div class="page-card">
        <div class="page-section">
          <h2><i class="fa-solid fa-volume-high" style={{ "margin-right": "8px", opacity: "0.6" }}></i><Text val="SETTINGS_MSG_SOUND" /></h2>

          <label class="permission-item" style={{ cursor: "pointer", "max-width": "fit-content" }} onClick={handleSoundToggle}>
            <input type="checkbox" checked={messageSoundEnabled()} />
            <div class="v" style={{ gap: "2px" }}>
              <span><Text val="SETTINGS_MSG_SOUND_LABEL" /></span>
              <span class="gray" style={{ "font-size": "var(--font-size-xs)", "font-weight": "normal" }}>
                <Text val="SETTINGS_MSG_SOUND_DESC" />
              </span>
            </div>
          </label>

          <Show when={messageSoundEnabled()}>
            <button class="btn btn-secondary" style={{ "margin-top": "var(--spacing-sm)", "max-width": "fit-content" }} onClick={() => playMessageSound()}>
              <i class="fa-solid fa-play" /> <Text val="SETTINGS_MSG_SOUND_TEST" />
            </button>
          </Show>
        </div>
      </div>

      <div class="page-card">
        <div class="page-section">
          <h2><i class="fa-solid fa-cube" style={{ "margin-right": "8px", opacity: "0.6" }}></i><Text val="SETTINGS_INVENTORY" /></h2>

          <label class="permission-item" style={{ cursor: "pointer", "max-width": "fit-content" }} onClick={handlePrettifyToggle}>
            <input type="checkbox" checked={prettify()} />
            <div class="v" style={{ gap: "2px" }}>
              <span><Text val="SETTINGS_PRETTIFY_QUANTITIES" /></span>
              <span class="gray" style={{ "font-size": "var(--font-size-xs)", "font-weight": "normal" }}>
                <Text val="SETTINGS_PRETTIFY_DESC" />
              </span>
            </div>
          </label>

          <div class="fcard" style={{ "max-width": "400px", "margin-top": "var(--spacing-sm)" }}>
            <span class="gray" style={{ "font-size": "var(--font-size-sm)" }}><Text val="SETTINGS_PRETTIFY_PREVIEW" />:</span>
            <div class="v" style={{ gap: "4px", "margin-top": "var(--spacing-sm)" }}>
              <span style={{ "font-family": "var(--font-family-mono)", "font-size": "var(--font-size-sm)" }}>
                3456 → {prettify() ? "1 DK" : "3,456"}
              </span>
              <span style={{ "font-family": "var(--font-family-mono)", "font-size": "var(--font-size-sm)" }}>
                192 → {prettify() ? "3 Stacks" : "192"}
              </span>
              <span style={{ "font-family": "var(--font-family-mono)", "font-size": "var(--font-size-sm)" }}>
                7103 → {prettify() ? "2 DK + 2 Stacks + 63" : "7,103"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
